import {
    IconDownload,
    IconHistory,
    IconEye,
    IconPlus,
    IconPencil,
    IconTrash,
    IconMessage,
    IconReceipt,
    IconCircleCheck,
    IconMinus,
} from '@tabler/icons-react';
import { Button, Table, Text } from '@fragments';
import './styles.scss';

const LOG_TYPE_MAP = {
    'community.created':       { label: 'Criou a comunidade',        icon: <IconPlus size={13} /> },
    'community.edited':        { label: 'Editou a comunidade',        icon: <IconPencil size={13} /> },
    'community.deleted':       { label: 'Excluiu a comunidade',       icon: <IconTrash size={13} /> },
    'content.created':         { label: 'Criou um conteúdo',          icon: <IconPlus size={13} /> },
    'content.updated':         { label: 'Atualizou um conteúdo',      icon: <IconPencil size={13} /> },
    'content.deleted':         { label: 'Excluiu um conteúdo',        icon: <IconTrash size={13} /> },
    'content.viewed':          { label: 'Visualizou um conteúdo',     icon: <IconEye size={13} /> },
    'content_comment.created': { label: 'Comentou em um conteúdo',    icon: <IconMessage size={13} /> },
    'content_comment.updated': { label: 'Editou um comentário',       icon: <IconPencil size={13} /> },
    'content_comment.deleted': { label: 'Excluiu um comentário',      icon: <IconTrash size={13} /> },
    'playlist.created':        { label: 'Criou uma playlist',         icon: <IconPlus size={13} /> },
    'playlist.updated':        { label: 'Atualizou uma playlist',     icon: <IconPencil size={13} /> },
    'playlist.deleted':        { label: 'Excluiu uma playlist',       icon: <IconTrash size={13} /> },
    'section.created':         { label: 'Criou uma seção',            icon: <IconPlus size={13} /> },
    'section.updated':         { label: 'Atualizou uma seção',        icon: <IconPencil size={13} /> },
    'section.deleted':         { label: 'Excluiu uma seção',          icon: <IconTrash size={13} /> },
    'subscription.created':    { label: 'Realizou uma assinatura',    icon: <IconReceipt size={13} /> },
    'subscription.paid':       { label: 'Assinatura paga',            icon: <IconCircleCheck size={13} /> },
    'subscription.canceled':   { label: 'Cancelou a assinatura',      icon: <IconTrash size={13} /> },
    'withdrawal.requested':    { label: 'Solicitou um saque',         icon: <IconReceipt size={13} /> },
    'withdrawal.configured':   { label: 'Configurou dados de saque',  icon: <IconPencil size={13} /> },
    'charge.refunded':         { label: 'Cobrança reembolsada',       icon: <IconPencil size={13} /> },
    'charge.paid':             { label: 'Cobrança paga',              icon: <IconCircleCheck size={13} /> },
    'group.created':           { label: 'Criou um grupo',             icon: <IconPlus size={13} /> },
    'group.updated':           { label: 'Atualizou um grupo',         icon: <IconPencil size={13} /> },
    'group.deleted':           { label: 'Excluiu um grupo',           icon: <IconTrash size={13} /> },
    'post.created':            { label: 'Publicou uma postagem',      icon: <IconPlus size={13} /> },
    'post.updated':            { label: 'Editou uma postagem',        icon: <IconPencil size={13} /> },
    'post.deleted':            { label: 'Excluiu uma postagem',       icon: <IconTrash size={13} /> },
    'material.created':        { label: 'Criou um material',          icon: <IconPlus size={13} /> },
    'material.updated':        { label: 'Atualizou um material',      icon: <IconPencil size={13} /> },
    'material.deleted':        { label: 'Excluiu um material',        icon: <IconTrash size={13} /> },
    'material.downloaded':     { label: 'Baixou o material',          icon: <IconDownload size={13} /> },
    'material.viewed':         { label: 'Visualizou o material',      icon: <IconEye size={13} /> },
};

function formatLogType(type) {
    return LOG_TYPE_MAP[type] ?? { label: type, icon: <IconMinus size={13} /> };
}

function formatDate(dateStr) {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('pt-BR', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

const columns = [
    {
        id: 'type',
        title: 'Ação',
        minWidth: 180,
        customRender: (item) => {
            const { label, icon } = formatLogType(item.type);
            return (
                <span className="log-type-badge">
                    {icon}
                    {label}
                </span>
            );
        },
    },
    {
        id: 'details',
        title: 'Detalhes',
        grow: 1,
        customRender: (item) => {
            const { label, material_id, ...rest } = item.details ?? {};
            const subtitle = material_id ?? Object.values(rest)[0] ?? null;
            return (
                <div className="log-detail-label">
                    {label
                        ? <Text.span contrast={7}>{label}</Text.span>
                        : <Text.span contrast={4}>—</Text.span>
                    }
                    {subtitle && <span className="log-detail-id">{subtitle}</span>}
                </div>
            );
        },
    },
    {
        id: 'created',
        title: 'Data',
        minWidth: 160,
        render: (item) => formatDate(item.timestamps?.created),
    },
];

export default function ChargebackLogsView({ username, logs, loading, pagination, fetchLogs, exportLogs }) {
    return (
        <div className="chargeback-logs mt-5">
            <div className="chargeback-logs-header">
                <Text.h3 left={<IconHistory size={22} />}>
                    Logs do usuário
                </Text.h3>
                <Button
                    variant="dashed"
                    size="small"
                    startIcon={<IconDownload size={15} />}
                    text="Exportar"
                    disabled={logs?.length === 0}
                    onClick={exportLogs}
                />
            </div>

            {username && (
                <Text.span contrast={5} className="mb-2" style={{ display: 'block' }}>
                    Visualize as principais ações de {username.split(' ')[0]} na plataforma. Você pode anexar as evidências solicitadas na aba de disputa.
                </Text.span>
            )}

            <Table
                id="chargeback-user-logs-table"
                columns={columns}
                data={logs}
                loading={loading}
                loadingRows={6}
                contained
                enablePagination
                pages={pagination.pages}
                records={pagination.records}
                page={pagination.page}
                onSelectPage={fetchLogs}
                emptyMessage="Nenhum log encontrado"
                emptySubtitle="Este usuário ainda não realizou nenhuma ação nesta comunidade."
            />
        </div>
    );
}
